import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Plus, Minus, Building2, ClipboardList, Landmark, Ruler, Wrench, Cog, Target, Factory, Package, Brain, LayoutGrid, Shield, Users, FileCheck, FileText, Presentation, Lock, Eye, Award, Sparkles, CheckCircle2, ArrowRight } from "lucide-react";
import PariksaLogo from "../components/PariksaLogo";
import PariksaMaturitySpectrum from "../components/PariksaMaturitySpectrum";

/* ═══════════════════════════════════════════════════════════════
   ASSAYO — AI-Powered Enterprise Maturity Diagnostics Platform
   Route: /platforms/assayo
   Flagship treatment — 12 sections
   ═══════════════════════════════════════════════════════════════ */

function CountUp({ end, prefix = "", suffix = "", duration = 1600, decimals = 0 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const t0 = performance.now();
          const tick = (t) => {
            const p = Math.min(1, (t - t0) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(end * eased);
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [end, duration]);
  const d = decimals ? val.toFixed(decimals) : Math.round(val).toLocaleString("en-IN");
  return <span ref={ref}>{prefix}{d}{suffix}</span>;
}

/* ─── Data ─── */

const TEMPLATES = [
  { slug: "cpma", name: "CPMA", full: "Capital Project Maturity Assessment", Icon: Building2, questions: "Comprehensive evaluation", tags: "FEL \u00b7 EVM \u00b7 AWP \u00b7 Commissioning" },
  { slug: "pm-pd-skill", name: "PM/PD Skill", full: "Project Manager / Project Director Skill Assessment", Icon: ClipboardList, questions: "206-question skill assessment", tags: "Leadership \u00b7 Planning \u00b7 Execution" },
  { slug: "pcm-skill", name: "PCM Skill", full: "Project Control Management Skill Assessment", Icon: Ruler, questions: "198-question skill assessment", tags: "Cost \u00b7 Schedule \u00b7 Risk" },
  { slug: "eng-mgr-skill", name: "Eng Manager Skill", full: "Engineering Manager Skill Assessment", Icon: Cog, questions: "201-question skill assessment", tags: "Technical \u00b7 Leadership \u00b7 Delivery" },
  { slug: "bd-tendering", name: "BD & Tendering", full: "Business Development & Tendering Dept Maturity", Icon: Target, questions: "204-question assessment", tags: "Pre-bid \u00b7 Win strategy \u00b7 Go/No-Go" },
  { slug: "precomm", name: "PreComm & Comm", full: "PreCommissioning & Commissioning Dept Maturity", Icon: Factory, questions: "200-question assessment", tags: "PreComm \u00b7 PCPM \u00b7 Handover" },
  { slug: "pc-pm-dept", name: "PC & PM Dept", full: "Project Controls & Project Management Dept Maturity", Icon: ClipboardList, questions: "200-question assessment", tags: "Controls \u00b7 PMO \u00b7 Governance" },
  { slug: "fab-construction", name: "Fab & Construction", full: "Fabrication and Construction Dept Maturity", Icon: Building2, questions: "200-question assessment", tags: "Fabrication \u00b7 Erection \u00b7 QC" },
  { slug: "pscm-dept", name: "PSCM Dept", full: "Procurement, Supply Chain & Contracts Dept Maturity", Icon: Package, questions: "150+ question assessment", tags: "Sourcing \u00b7 Contracts \u00b7 Materials" },
  { slug: "eng-dept", name: "Engineering Dept", full: "Engineering Department Maturity Assessment", Icon: Cog, questions: "Comprehensive assessment", tags: "Design \u00b7 Review \u00b7 Deliverables" },
  { slug: "ai-readiness", name: "AI Readiness", full: "AI Readiness in Capital Projects", Icon: Brain, questions: "Comprehensive assessment", tags: "AI \u00b7 ML \u00b7 Digital \u00b7 Data" },
  { slug: "prefeed-feed", name: "PreFEED & FEED", full: "PreFEED & FEED Maturity Assessment", Icon: Wrench, questions: "Comprehensive assessment", tags: "FEL-1/2/3 \u00b7 PDRI-gated" },
  { slug: "detailed-eng", name: "Detailed Eng", full: "Detailed Engineering Maturity Assessment", Icon: Cog, questions: "Comprehensive assessment", tags: "P&ID \u00b7 3D \u00b7 MTO \u00b7 DED" },
  { slug: "pdri", name: "PDRI", full: "PDRI Assessment", Icon: Ruler, questions: "Project Definition Rating Index", tags: "Scope definition \u00b7 FEL" },
  { slug: "pscm-maturity", name: "PSCM Maturity", full: "Procurement & Supply Chain Maturity Assessment", Icon: Package, questions: "Comprehensive assessment", tags: "S2P \u00b7 Kraljic \u00b7 Category" },
  { slug: "bim-cde", name: "BIM & CDE", full: "BIM & CDE Maturity Assessment", Icon: Landmark, questions: "Comprehensive BIM & CDE", tags: "ISO 19650 \u00b7 CDE \u00b7 Info Mgmt" },
  { slug: "operational-readiness", name: "Operational Readiness", full: "Operational Readiness Assessment", Icon: Shield, questions: "Readiness evaluation", tags: "Safe \u00b7 Efficient \u00b7 Startup" },
  { slug: "custom", name: "Custom", full: "Your Own Template", Icon: LayoutGrid, questions: "Author from scratch", tags: "Visual editor \u00b7 Bulk import" },
];

const STAGES = [
  {
    num: "01", title: "Select & Scope", days: "Day 1",
    desc: "Pick your diagnostic template. Define what\u2019s in scope.",
    features: [
      "Choose from 17 built-in industry templates or clone any published template to create a custom version",
      "Scope specific domains and sub-domains \u2014 not every assessment needs the full 80-question template",
      "Assemble your assessment team: owner, respondents by domain, reviewers",
      "Calibrate weights \u2014 if governance matters more than execution for your context, override defaults",
      "Preview token cost before activation",
      "Activation guard prevents starting with incomplete scope",
    ],
  },
  {
    num: "02", title: "Execute & Gather", days: "Days 2\u20133",
    desc: "Each respondent sees only their assigned domains.",
    features: [
      "Respondents log in and see personalized question views",
      "Likert (1\u20135 / 1\u201310), multi-select, numeric, and free-text question types",
      "Evidence upload per question \u2014 PDF, DOCX, XLSX, PNG",
      "AI auto-parses uploaded evidence into structured inline justification text",
      "Reviewer gate \u2014 reviewers validate before scoring locks",
      "Owner dashboard tracks respondent progress in real time",
      "Automated reminder emails for laggards via Resend",
    ],
  },
  {
    num: "03", title: "Score & Diagnose", days: "Day 4",
    desc: "Hierarchical 0\u2013100% maturity scoring rolls up automatically.",
    features: [
      "Question \u2192 Competency \u2192 Sub-domain \u2192 Domain \u2192 Overall",
      "Configurable weights at every level",
      "Per-domain target vs actual gap analysis, severity-flagged",
      "AI-generated qualitative gap narratives (Claude Opus 4.6)",
      "Effort\u2013Value 2\u00d72 matrix for initiative prioritization",
      "Benchmark comparison against industry peers from seeded data",
    ],
  },
  {
    num: "04", title: "Roadmap & Report", days: "Day 5",
    desc: "AI generates your transformation plan. Export to PDF or PPTX.",
    features: [
      "10\u201320 AI-proposed transformation initiatives per assessment",
      "Phased roadmap: Short / Medium / Long-term waves with dependencies",
      "Interactive ROI calculator with editable fiscal-year grid (3/5/7/10-year horizons)",
      "Live NPV, IRR, Payback recomputation on every edit",
      "AI benefit estimates with confidence intervals + user override capability",
      "Branded PDF and PPTX exports: cover \u2192 exec summary \u2192 heatmap \u2192 gaps \u2192 roadmap \u2192 ROI",
    ],
  },
];

const FAQS = [
  { q: "How is Assayo different from SurveyMonkey or typeform-based surveys?", a: "Assayo is not a survey tool. It\u2019s a structured enterprise diagnostic with hierarchical weighted scoring, role-based team workflows, evidence trails, reviewer gates, AI-generated roadmaps, interactive ROI calculators, and board-ready report exports. SurveyMonkey gives you collected responses. Assayo gives you a consulting-grade capability assessment deliverable. Different category entirely." },
  { q: "How is Assayo different from hiring a top-tier consulting firm?", a: "Tier-1 consulting engagements produce bespoke insight but cost \u20b960\u2013150 Lakh and take 10\u201314 weeks. Assayo produces 80% of that rigor for 2\u20135% of the cost in 5\u201310% of the time. We recommend a consulting firm for novel, high-stakes, or judgment-heavy diagnostics. For standard enterprise capability assessments, Assayo is faster, cheaper, and more repeatable." },
  { q: "Can I trust a 5-day self-service assessment for a \u20b9500 Cr decision?", a: "Assayo\u2019s methodology is based on industry-standard maturity frameworks (CII Best Practices, PMI, ISO 19650, PDRI). The rigor is in the question structure, weighted scoring math, and AI-generated gap analysis. Your contribution is domain judgment \u2014 which is exactly what a senior consultant would do too. The combined output is defensible, cited, and auditable." },
  { q: "What if my organization needs a template that isn\u2019t one of the 17 built-in ones?", a: "Professional tier includes custom template authoring. Clone any published template, modify domains and questions, add AI-generated maturity descriptors via Claude, and publish a tailored version. Enterprise tier includes Capexara\u2019s team helping author the template from scratch." },
  { q: "Who should be the respondents? What if they\u2019re not experts?", a: "Respondents should be the people who actually perform the work in each domain. The assessment OWNER reviews and contextualizes the responses. Assayo\u2019s evidence-upload and inline-justification features keep responses honest and defensible. The Reviewer role acts as a quality gate." },
  { q: "How does the AI-generated roadmap avoid hallucination?", a: "The roadmap is grounded entirely in YOUR scored assessment data \u2014 there\u2019s minimal room for hallucination because the AI is synthesizing structured inputs (maturity scores, evidence, gap patterns) rather than generating factual claims. You can regenerate, edit, or discard any initiative. Every AI-generated number in the ROI calculator is editable with user override tracking." },
  { q: "Can we run assessments for multiple departments / business units?", a: "Yes. Professional and Enterprise tiers support unlimited parallel assessments. Each can have different templates, teams, scopes, and weights. Multi-assessment dashboards roll up results across the organization." },
  { q: "What integrations exist?", a: "Direct REST API access for Professional and Enterprise tiers. Current integrations: Resend (email), Razorpay (payments), Anthropic SDK (AI). Roadmap: Slack, Microsoft Teams, Google Drive, SharePoint, and Panorix integration for transformation execution tracking." },
  { q: "How is data security handled?", a: "Three deployment modes: (1) Shared Azure cloud with row-level org_id enforcement; (2) Dedicated Azure with India/EU data residency for DPDP/GDPR; (3) On-premise for Defense, PSU, and sensitive engagements. All evidence uploads encrypted at rest." },
  { q: "What happens if we run out of tokens mid-assessment?", a: "Tokens are deducted at assessment ACTIVATION, not during execution. Once activated, you have full access to complete the assessment regardless of token balance changes." },
  { q: "Can we try Assayo before committing?", a: "Yes. Every new account gets 1 free token on signup \u2014 enough to run a complete assessment with full AI-generated roadmap, ROI calculator, PDF/PPTX exports. No credit card required." },
  { q: "Who owns the assessment data?", a: "Your organization owns all assessment data. Assayo retains anonymized aggregate data only for improving benchmark statistics. On account termination, you can export everything and request permanent deletion." },
];

const PERSONAS = [
  { role: "Chief Projects Officer / CPO", quote: "I need to diagnose the maturity of my capital project delivery function before committing \u20b9500 Cr to the next program.", templates: "CPMA, PMPD, PDRI, PreFEED/FEED", use: "Portfolio-wide capability diagnostic", output: "Gap-to-best-in-class + transformation roadmap" },
  { role: "Chief Transformation Officer / CTO", quote: "I need a defensible baseline and roadmap for the Digital Transformation agenda the board just funded.", templates: "Digital Transformation, Custom", use: "Enterprise transformation diagnostic", output: "Baseline maturity + phased transformation plan" },
  { role: "Chief Procurement Officer / CPO", quote: "I need to know where our procurement maturity stands against peers before investing in a new S2P platform.", templates: "PSCM", use: "Procurement function diagnostic", output: "Maturity gaps + platform investment business case" },
  { role: "Strategy Consultants", quote: "I need to accelerate my client\u2019s capability assessment from 10 weeks to 2 weeks so I can spend more time on transformation.", templates: "Any, plus white-label", use: "Delivery accelerator for consulting firms", output: "Branded client deliverable in firm\u2019s template" },
  { role: "PMO / Portfolio Head", quote: "I need to run annual capability diagnostics across my portfolio \u2014 but I can\u2019t afford a consulting firm every year.", templates: "CPMA, PMPD, PDRI", use: "Annual/semi-annual capability baseline", output: "Year-over-year maturity trending" },
];

/* ═══════════════════════════════════════════════════════════════ */

export default function PlatformPariksa() {
  return (
    <>
      <S1Hero />
      <S2Crisis />
      <S3Workflow />
      <S4Templates />
      <S5RoadmapRoi />
      <S6Execution />
      <S7Reports />
      <S8Competitive />
      <S9Personas />
      <S11Faq />
      <S12Cta />
    </>
  );
}

/* ═══════ S1 — HERO ═══════════════════════════════════════════ */
function S1Hero() {
  return (
    <section className="grantha-hero pk-hero" data-testid="pk-hero">
      <div className="container grantha-hero-grid">
        <div className="grantha-hero-copy">
          <Link to="/platforms" className="detail-back" data-testid="pk-back">
            <ChevronLeft size={14} /> All platforms
          </Link>
          <div className="grantha-crumb mono" data-testid="pk-breadcrumb">Platforms / AI-Powered Consulting / ASSAYO</div>
          <div className="eyebrow pk-eyebrow" data-testid="pk-eyebrow">Enterprise Maturity Diagnostics</div>
          <h1 className="grantha-h1 pk-h1" data-testid="pk-h1">
            <span className="grantha-indo pk-logo-lockup">
              <PariksaLogo size={56} variant="icon" />
              <span className="pk-wordmark">Assa<span className="pk-k">y</span>o</span>
            </span>
            <span className="grantha-h1-en pk-h1-en">
              The assessment every C-suite asks.{" "}
              <em className="pk-days-em">Answered in days.</em>
            </span>
          </h1>
          <p className="grantha-sub" data-testid="pk-hero-sub">
            {"Seventeen industry-grade maturity diagnostics. AI-generated transformation roadmaps. Editable ROI business cases. Board-ready PDF and PPTX exports. What consulting firms deliver in 10 weeks for \u20b960\u2013150 Lakh \u2014 Assayo delivers in 5 days for a fraction of the cost."}
          </p>
          <div className="grantha-ctas" data-testid="pk-ctas">
            <a href="https://assayo.capexara.com/" target="_blank" rel="noopener noreferrer" className="btn btn-primary" data-testid="pk-cta-access">Access Assayo <span className="arrow">→</span></a>
            <Link to="/contact?p=assayo" className="btn btn-ghost" data-testid="pk-cta-primary">Book a Demo <span className="arrow">→</span></Link>
            <a href="#reports" className="btn btn-ghost" data-testid="pk-cta-ghost">See How It Works</a>
          </div>
          <div className="grantha-trust mono" data-testid="pk-trust">
            {"ISO 19650 \u00b7 PMI \u00b7 PDRI \u00b7 CII Best Practices \u00b7 CPMA \u00b7 PSCM \u00b7 17 built-in templates"}
          </div>
        </div>
        <div className="grantha-hero-stage pk-hero-stage" data-testid="pk-hero-viz">
          <PariksaMaturitySpectrum />
        </div>
      </div>
    </section>
  );
}

/* ═══════ S2 — THE CAPABILITY ASSESSMENT CRISIS ═══════════════ */
function S2Crisis() {
  const stats = [
    { n: 60, prefix: "\u20b9", suffix: "\u2013150 L", label: "typical consulting-firm capability assessment cost" },
    { n: 10, suffix: "\u201314 weeks", label: "typical time to deliver a capability assessment" },
    { n: 6, suffix: "\u201312 people", label: "consulting team size for a single assessment engagement" },
    { n: 50, suffix: "\u201380%", label: "of value lost in the handoff back to the client" },
  ];
  return (
    <section className="grantha-problem pk-crisis" data-testid="pk-crisis">
      <div className="container">
        <div className="eyebrow" style={{ color: "var(--ember)" }}>The diagnostic bottleneck</div>
        <h2 className="grantha-problem-h" data-testid="pk-crisis-h2">
          <em>Every C-suite has commissioned a maturity assessment. Most regret how long and how expensive it took.</em>
        </h2>
        <div className="grantha-problem-grid" data-testid="pk-crisis-stats">
          {stats.map((s, i) => (
            <div className="grantha-problem-tile" key={i} data-testid={`pk-stat-${i}`}>
              <div className="grantha-problem-num">
                <CountUp end={s.n} prefix={s.prefix || ""} suffix={s.suffix || ""} />
              </div>
              <div className="grantha-problem-label">{s.label}</div>
            </div>
          ))}
        </div>
        <p className="grantha-problem-quote" data-testid="pk-crisis-quote">
          The assessment should take a week. <em>The transformation is what takes years.</em>
        </p>
      </div>
    </section>
  );
}

/* ═══════ S3 — FOUR-STAGE ASSESSMENT WORKFLOW ═════════════════ */
function S3Workflow() {
  const [active, setActive] = useState(0);
  return (
    <section className="pk-workflow" data-testid="pk-workflow">
      <div className="container">
        <div className="eyebrow">The method</div>
        <h2 className="grantha-arch-h" data-testid="pk-workflow-h2">Four stages. Five days. <em>One board-ready deliverable.</em></h2>
        <div className="pk-stages" data-testid="pk-stages">
          {STAGES.map((s, i) => {
            const isActive = active === i;
            return (
              <div key={i} className={`pk-stage ${isActive ? "pk-stage-active" : ""}`} data-testid={`pk-stage-${i}`} onClick={() => setActive(i)}>
                <div className="pk-stage-header">
                  <div className="pk-stage-num">{s.num}</div>
                  <div className="pk-stage-meta">
                    <div className="pk-stage-title">{s.title}</div>
                    <div className="pk-stage-desc">{s.desc}</div>
                  </div>
                  <span className="pk-stage-chip mono">{s.days}</span>
                  <span className="pk-stage-toggle">{isActive ? <Minus size={18} /> : <Plus size={18} />}</span>
                </div>
                {isActive && (
                  <ul className="pk-stage-features" data-testid={`pk-stage-features-${i}`}>
                    {s.features.map((f, j) => (
                      <li key={j}><CheckCircle2 size={14} className="pk-check" /> {f}</li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
        <p className="pk-workflow-note mono" data-testid="pk-workflow-note">
          {"Typical consulting-firm engagement: 10\u201314 weeks. Assayo: 5 days. Same methodological rigor. Same deliverable quality."}
        </p>
      </div>
    </section>
  );
}

/* ═══════ S4 — TEMPLATE LIBRARY ═══════════════════════════════ */
function S4Templates() {
  return (
    <section className="pk-templates" data-testid="pk-templates">
      <div className="container">
        <div className="eyebrow" style={{ color: "var(--steel-200)" }}>17 industry-grade diagnostics</div>
        <h2 className="pk-templates-h" data-testid="pk-templates-h2">Not one generic template. <em>Seventeen purpose-built</em> for the work you actually do.</h2>
        <div className="pk-template-grid" data-testid="pk-template-grid">
          {TEMPLATES.map((t, i) => (
            <div key={t.slug} className={`pk-tpl-card ${t.slug === "custom" ? "pk-tpl-custom" : ""}`} data-testid={`pk-tpl-${t.slug}`} style={{ animationDelay: `${i * 80}ms` }}>
              <t.Icon size={32} className="pk-tpl-icon" />
              <div className="pk-tpl-name">{t.name}</div>
              <div className="pk-tpl-full">{t.full}</div>
              <div className="pk-tpl-meta mono">{t.questions}</div>
              <div className="pk-tpl-tags">{t.tags}</div>
            </div>
          ))}
        </div>
        <div className="pk-template-supplement" data-testid="pk-template-supplement">
          <div className="pk-supp-col">
            <h3 className="pk-supp-title">Hierarchical scoring</h3>
            <p className="pk-supp-body">{"Every template follows the same four-level hierarchy: Domain \u2192 Sub-domain \u2192 Competency \u2192 Question. Every level has weighted scoring. Every question has 1\u20135 maturity descriptors. Every descriptor is AI-generated and editable."}</p>
          </div>
          <div className="pk-supp-col">
            <h3 className="pk-supp-title">Template authoring</h3>
            <p className="pk-supp-body">{"Every template is editable. Clone any published template, customize it for your org, add your own domains and questions, and publish a tailored version \u2014 all through the visual Template Editor. Bulk import from Excel. AI-generated maturity descriptors via Claude Opus 4.6."}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════ S5 — AI ROADMAP & ROI CALCULATOR ════════════════════ */
function S5RoadmapRoi() {
  return (
    <section className="pk-roadmap-roi" data-testid="pk-roadmap-roi">
      <div className="container">
        <div className="eyebrow">From scores to action</div>
        <h2 className="grantha-arch-h" data-testid="pk-roadmap-h2">AI turns your assessment into a <em>boardroom-ready transformation plan.</em></h2>
        <div className="pk-rr-split" data-testid="pk-rr-split">
          {/* LEFT — Roadmap */}
          <div className="pk-rr-left" data-testid="pk-roadmap-panel">
            <div className="pk-mockup-header mono">{"Transformation Roadmap \u2014 generated by Claude Opus 4.6"}</div>
            <div className="pk-roadmap-phases">
              <div className="pk-phase">
                <div className="pk-phase-label mono">{"Phase 1 \u2014 Short Term (0\u20136 months)"}</div>
                {[
                  { name: "Establish Stage-Gate Governance Framework", e: "S", i: "H", w: "12 wks" },
                  { name: "Deploy Digital Project Controls Platform", e: "M", i: "H", w: "16 wks" },
                  { name: "Roll out AWP Pilot Program", e: "S", i: "M", w: "10 wks" },
                ].map((init, j) => (
                  <div key={j} className="pk-init-pill">
                    <span className="pk-init-name">{init.name}</span>
                    <span className="pk-init-tags mono">E:{init.e} {"\u00b7"} I:{init.i} {"\u00b7"} {init.w}</span>
                  </div>
                ))}
              </div>
              <div className="pk-phase">
                <div className="pk-phase-label mono">{"Phase 2 \u2014 Medium Term (6\u201318 months)"}</div>
                {[
                  { name: "Integrated Digital Twin for Critical Assets", e: "L", i: "H", w: "32 wks" },
                  { name: "AI-Powered Risk Prediction Engine", e: "M", i: "M", w: "22 wks" },
                  { name: "Portfolio Governance Overhaul", e: "M", i: "H", w: "26 wks" },
                ].map((init, j) => (
                  <div key={j} className="pk-init-pill">
                    <span className="pk-init-name">{init.name}</span>
                    <span className="pk-init-tags mono">E:{init.e} {"\u00b7"} I:{init.i} {"\u00b7"} {init.w}</span>
                  </div>
                ))}
              </div>
              <div className="pk-phase">
                <div className="pk-phase-label mono">{"Phase 3 \u2014 Long Term (18\u201336 months)"}</div>
                {[
                  { name: "Generative Engineering Capability Build", e: "L", i: "VH", w: "46 wks" },
                  { name: "Autonomous Commissioning Platform", e: "L", i: "H", w: "52 wks" },
                ].map((init, j) => (
                  <div key={j} className="pk-init-pill">
                    <span className="pk-init-name">{init.name}</span>
                    <span className="pk-init-tags mono">E:{init.e} {"\u00b7"} I:{init.i} {"\u00b7"} {init.w}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="pk-rr-callout mono">{"AI-generated \u00b7 10\u201320 initiatives per assessment \u00b7 Effort, impact, duration tagged \u00b7 Use-cases mapped"}</div>
          </div>
          {/* RIGHT — ROI Calculator */}
          <div className="pk-rr-right" data-testid="pk-roi-panel">
            <div className="pk-mockup-header mono">{"ROI Calculator \u2014 Digital Project Controls Platform"}</div>
            <div className="pk-roi-grid">
              <table className="pk-roi-table">
                <thead>
                  <tr><th></th><th className="mono">Y1</th><th className="mono">Y2</th><th className="mono">Y3</th><th className="mono">Y4</th><th className="mono">Y5</th></tr>
                </thead>
                <tbody>
                  <tr><td>CapEx</td><td className="mono">{"\u20b94.2 Cr"}</td><td className="mono">{"\u20b90.8 Cr"}</td><td className="mono">{"\u20b90.3 Cr"}</td><td className="mono pk-cell-dash">{"\u2014"}</td><td className="mono pk-cell-dash">{"\u2014"}</td></tr>
                  <tr><td>OpEx</td><td className="mono">{"\u20b91.8 Cr"}</td><td className="mono">{"\u20b92.1 Cr"}</td><td className="mono">{"\u20b92.3 Cr"}</td><td className="mono">{"\u20b92.5 Cr"}</td><td className="mono">{"\u20b92.7 Cr"}</td></tr>
                  <tr className="pk-roi-benefit"><td>Benefits</td><td className="mono">{"\u20b90.0 Cr"}</td><td className="mono">{"\u20b98.5 Cr"}</td><td className="mono">{"\u20b914.2 Cr"}</td><td className="mono">{"\u20b917.1 Cr"}</td><td className="mono">{"\u20b917.8 Cr"}</td></tr>
                  <tr><td>Ramp</td><td className="mono">0%</td><td className="mono">50%</td><td className="mono">85%</td><td className="mono">100%</td><td className="mono">100%</td></tr>
                  <tr className="pk-roi-net"><td>Net</td><td className="mono pk-neg">{"\u2212\u20b96.0 Cr"}</td><td className="mono pk-pos">{"\u20b95.6 Cr"}</td><td className="mono pk-pos">{"\u20b911.6 Cr"}</td><td className="mono pk-pos">{"\u20b914.6 Cr"}</td><td className="mono pk-pos">{"\u20b915.1 Cr"}</td></tr>
                </tbody>
              </table>
              <div className="pk-roi-summary mono" data-testid="pk-roi-summary">
                <span>NPV (10%): <strong>{"\u20b932.4 Cr"}</strong></span>
                <span>IRR: <strong>68%</strong></span>
                <span>Payback: <strong>2.1 years</strong></span>
              </div>
            </div>
            <div className="pk-rr-callout mono">{"AI-generated benefit estimates \u00b7 Editable grid \u00b7 Live NPV/IRR/Payback \u00b7 3/5/7/10-year horizons"}</div>
          </div>
        </div>
        <p className="grantha-problem-quote pk-rr-quote" data-testid="pk-rr-quote">
          The business case your CFO will approve. <em>Built from the assessment data you already have.</em>
        </p>
      </div>
    </section>
  );
}

/* ═══════ S6 — ASSESSMENT EXECUTION EXPERIENCE ════════════════ */
function S6Execution() {
  const cols = [
    {
      Icon: Shield, title: "4 roles. Graded permissions. Multi-tenant isolation.",
      items: [
        "Admin \u2014 full org control, IAM management",
        "Assessor \u2014 create and execute assessments",
        "Reviewer \u2014 validate responses before scoring locks",
        "Viewer \u2014 read-only access to reports",
        "Invitation-based member onboarding via email",
        "JWT-based sessions with /auth/me refresh",
        "Row-level org_id enforcement at the query layer",
      ],
    },
    {
      Icon: Users, title: "Personalized views. Evidence trails. Reviewer gates.",
      items: [
        "Respondents only see their assigned domains",
        "Per-respondent progress dashboards for the owner",
        "Evidence upload per question (PDF/DOCX/XLSX/PNG)",
        "AI auto-parses evidence into inline justifications",
        "Reviewer validation gate before scoring finalizes",
        "Automated reminder emails to laggards",
        "Bulk response submission per domain",
      ],
    },
    {
      Icon: FileCheck, title: "Every response traceable. Every decision defensible.",
      items: [
        "Every response timestamped with respondent ID",
        "Evidence attachments permanently linked to responses",
        "Scoring rollups computed deterministically",
        "Reviewer-gate decisions logged",
        "Token transaction audit trail",
        "Every AI-generated number traces to its source",
        "Version history for assessment configurations",
      ],
    },
  ];
  return (
    <section className="pk-execution" data-testid="pk-execution">
      <div className="container">
        <div className="eyebrow" style={{ color: "var(--steel-200)" }}>Built for enterprise execution</div>
        <h2 className="pk-templates-h" data-testid="pk-execution-h2">Not a survey. A structured diagnostic with <em>team, roles, evidence, and governance.</em></h2>
        <div className="pk-exec-grid" data-testid="pk-exec-grid">
          {cols.map((c, i) => (
            <div key={i} className="pk-exec-col" data-testid={`pk-exec-col-${i}`}>
              <c.Icon size={28} className="pk-exec-icon" />
              <h3 className="pk-exec-title">{c.title}</h3>
              <ul className="pk-exec-list">
                {c.items.map((item, j) => <li key={j}>{item}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════ S7 — ENTERPRISE REPORT GENERATION ═══════════════════ */
function S7Reports() {
  const pages = ["Cover Page", "Executive Summary", "Maturity Heatmap", "Domain Deep-Dives", "Gap Analysis", "Transformation Roadmap", "ROI Business Case", "Appendices"];
  const [pageIdx, setPageIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setPageIdx(p => (p + 1) % pages.length), 4000);
    return () => clearInterval(t);
  }, [pages.length]);
  return (
    <section className="pk-reports" id="reports" data-testid="pk-reports">
      <div className="container">
        <div className="eyebrow">Board-ready deliverables</div>
        <h2 className="grantha-arch-h" data-testid="pk-reports-h2">The consulting deliverable your CEO expects. <em>Generated in minutes.</em></h2>
        <div className="pk-reports-split" data-testid="pk-reports-split">
          {/* LEFT — Report mockup */}
          <div className="pk-report-stack" data-testid="pk-report-stack">
            {pages.map((pg, i) => {
              const offset = (i - pageIdx + pages.length) % pages.length;
              const z = pages.length - offset;
              return (
                <div key={pg} className="pk-report-page" style={{
                  zIndex: z,
                  transform: `perspective(800px) rotateY(${offset === 0 ? 0 : -4}deg) rotateX(${offset * 2}deg) translateZ(${-offset * 8}px) translateY(${offset * 6}px)`,
                  opacity: offset > 3 ? 0 : 1 - offset * 0.15,
                }}>
                  <div className="pk-rp-bar" />
                  <div className="pk-rp-title mono">{pg}</div>
                  <div className="pk-rp-lines">
                    {Array.from({ length: 4 }).map((_, k) => <div key={k} className="pk-rp-line" style={{ width: `${60 + Math.random() * 30}%` }} />)}
                  </div>
                  {offset === 0 && <div className="pk-rp-badge mono">{"AI-generated \u00b7 editable \u00b7 branded"}</div>}
                </div>
              );
            })}
          </div>
          {/* RIGHT — Capabilities */}
          <div className="pk-reports-right" data-testid="pk-reports-right">
            <h3 className="pk-reports-right-h">Two formats. One click. Both board-ready.</h3>
            {[
              { Icon: FileText, title: "PDF Export (ReportLab)", desc: "40\u201360 page branded report: cover \u2192 exec summary \u2192 maturity heatmap \u2192 gaps \u2192 roadmap \u2192 ROI \u2192 appendices." },
              { Icon: Presentation, title: "PPTX Export (python-pptx)", desc: "20\u201330 slides, board-deck formatted. Drop directly into your next leadership review." },
              { Icon: Award, title: "Custom Branding (Enterprise)", desc: "Replace Assayo branding with your consulting firm\u2019s logo and color palette." },
              { Icon: Lock, title: "Evidence Appendix", desc: "Every scored response includes a direct reference to uploaded evidence, making the report fully defensible." },
              { Icon: Sparkles, title: "AI-Written Executive Summary", desc: "Claude Opus 4.6 generates the executive summary based on your assessment data \u2014 not a template filler." },
              { Icon: Eye, title: "Download API", desc: "Direct endpoint access for integration with your own workflows and report distribution systems." },
            ].map((f, i) => (
              <div key={i} className="pk-report-feat" data-testid={`pk-report-feat-${i}`}>
                <f.Icon size={18} className="pk-rf-icon" />
                <div>
                  <div className="pk-rf-title">{f.title}</div>
                  <div className="pk-rf-desc">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <p className="grantha-problem-quote pk-reports-quote" data-testid="pk-reports-quote">
          {"What consulting firms hand over in week 10 \u2014 "}<em>{"Assayo generates in minute 5 of day 5."}</em>
        </p>
      </div>
    </section>
  );
}

/* ═══════ S8 — COMPETITIVE FRAMING ════════════════════════════ */
function S8Competitive() {
  return (
    <section className="pk-competitive" data-testid="pk-competitive">
      <div className="container">
        <div className="eyebrow">{"Why Assayo"}</div>
        <h2 className="grantha-arch-h" data-testid="pk-comp-h2">Three ways to run a capability assessment. <em>Only one scales.</em></h2>
        <div className="pk-comp-grid" data-testid="pk-comp-grid">
          <div className="pk-comp-card pk-comp-alt" data-testid="pk-comp-consulting">
            <h3 className="pk-comp-name">Consulting Firm Capability Assessment</h3>
            <p className="pk-comp-firms mono">{"Tier-1 management & advisory firms"}</p>
            <ul className="pk-comp-list">
              <li><strong>Cost:</strong> {"\u20b960\u2013150 Lakh per engagement"}</li>
              <li><strong>Duration:</strong> {"10\u201314 weeks typical"}</li>
              <li><strong>Team:</strong> {"6\u201312 consultants"}</li>
              <li><strong>Output:</strong> PowerPoint deck + Excel model</li>
              <li><strong>Reusability:</strong> Single-use deliverable</li>
            </ul>
          </div>
          <div className="pk-comp-card pk-comp-alt" data-testid="pk-comp-excel">
            <h3 className="pk-comp-name">In-House Excel Templates</h3>
            <p className="pk-comp-firms mono">{"Free templates \u00b7 SharePoint \u00b7 SurveyMonkey"}</p>
            <ul className="pk-comp-list">
              <li><strong>Cost:</strong> Essentially free</li>
              <li><strong>Duration:</strong> {"2\u20136 weeks (if anyone finishes)"}</li>
              <li><strong>Team:</strong> Whoever has capacity</li>
              <li><strong>Output:</strong> Excel with ad-hoc narrative</li>
              <li><strong>Reusability:</strong> Accumulating technical debt</li>
            </ul>
          </div>
          <div className="pk-comp-card pk-comp-hero" data-testid="pk-comp-pariksa">
            <div className="pk-comp-spark" />
            <h3 className="pk-comp-name pk-comp-name-hero">{"Assayo (Capexara)"}</h3>
            <ul className="pk-comp-list">
              <li><strong>Cost:</strong> {"1 token per assessment (\u2248\u20b925,000\u201375,000)"}</li>
              <li><strong>Duration:</strong> 5 days typical</li>
              <li><strong>Team:</strong> {"As few as 3\u20135 people"}</li>
              <li><strong>Output:</strong> PDF + PPTX + live ROI calculator + reusable template</li>
              <li><strong>Reusability:</strong> Every assessment is a reusable template for the next one</li>
              <li><strong>Strength:</strong> {"Methodological rigor \u00d7 AI acceleration \u00d7 Enterprise governance \u00d7 Self-service"}</li>
            </ul>
          </div>
        </div>
        <p className="grantha-problem-quote pk-comp-quote" data-testid="pk-comp-quote">
          Consulting engagements give you judgment. Excel gives you speed. <em>{"Assayo gives you both"}</em> {"\u2014"} plus a reusable asset at the end.
        </p>
        <p className="pk-comp-note" data-testid="pk-comp-note">{"Assayo doesn\u2019t replace consulting firms \u2014 many consulting firms use Assayo internally to accelerate their own client deliveries. Enterprise tier includes white-labeling for exactly this use case."}</p>
      </div>
    </section>
  );
}

/* ═══════ S9 — WHO USES ASSAYO ══════════════════════════════ */
function S9Personas() {
  return (
    <section className="pk-personas" data-testid="pk-personas">
      <div className="container">
        <div className="eyebrow">Designed for</div>
        <h2 className="grantha-arch-h" data-testid="pk-personas-h2">Five buyer personas. <em>One platform.</em></h2>
        <div className="pk-persona-grid" data-testid="pk-persona-grid">
          {PERSONAS.map((p, i) => (
            <div key={i} className="pk-persona-card" data-testid={`pk-persona-${i}`}>
              <div className="pk-persona-role">{p.role}</div>
              <blockquote className="pk-persona-quote">{"\u201c"}{p.quote}{"\u201d"}</blockquote>
              <div className="pk-persona-detail">
                <div><strong>Templates:</strong> {p.templates}</div>
                <div><strong>Use:</strong> {p.use}</div>
                <div><strong>Output:</strong> {p.output}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════ S10 — PRICING & TOKEN MODEL ═════════════════════════ */
function S10Pricing() {
  return (
    <section className="pk-pricing" data-testid="pk-pricing">
      <div className="container">
        <div className="eyebrow">Pricing</div>
        <h2 className="grantha-arch-h" data-testid="pk-pricing-h2">One token per assessment. <em>Your first token is free.</em></h2>
        <div className="pk-price-grid" data-testid="pk-price-grid">
          {/* Starter */}
          <div className="pk-price-card" data-testid="pk-price-starter">
            <div className="pk-price-tier mono">Starter</div>
            <div className="pk-price-for">For single assessments</div>
            <div className="pk-price-amount">$299<span className="pk-price-per"> per token</span></div>
            <ul className="pk-price-features">
              <li>1 token (1 full assessment, any template)</li>
              <li>All 9 built-in templates</li>
              <li>AI-generated roadmap (Claude Opus 4.6)</li>
              <li>Interactive ROI calculator</li>
              <li>PDF + PPTX exports</li>
              <li>Up to 5 respondents per assessment</li>
              <li>Email support</li>
              <li className="pk-feat-highlight">First token FREE on signup</li>
            </ul>
            <Link to="/contact?p=assayo" className="btn btn-ghost pk-price-cta" data-testid="pk-cta-starter">{"Start Free Assessment"} <ArrowRight size={16} /></Link>
          </div>
          {/* Professional */}
          <div className="pk-price-card pk-price-recommended" data-testid="pk-price-pro">
            <div className="pk-price-badge mono">Most consulting teams choose this</div>
            <div className="pk-price-tier mono">Professional</div>
            <div className="pk-price-for">For transformation teams & consulting practices</div>
            <div className="pk-price-amount">$1,999<span className="pk-price-per"> for 10 tokens</span></div>
            <div className="pk-price-effective mono">{"$200 per token \u2014 33% savings"}</div>
            <ul className="pk-price-features">
              <li>Everything in Starter, plus:</li>
              <li>10 tokens per year</li>
              <li>Unlimited respondents per assessment</li>
              <li>Multi-assessment dashboards</li>
              <li>Custom template authoring</li>
              <li>Reviewer workflows</li>
              <li>Priority email support</li>
              <li>Evidence parsing quota (100 docs/month)</li>
            </ul>
            <Link to="/contact?p=assayo" className="btn btn-primary pk-price-cta" data-testid="pk-cta-pro">Buy Professional <ArrowRight size={16} /></Link>
          </div>
          {/* Enterprise */}
          <div className="pk-price-card" data-testid="pk-price-enterprise">
            <div className="pk-price-tier mono">Enterprise</div>
            <div className="pk-price-for">For consulting firms, global enterprises, Defense & PSU</div>
            <div className="pk-price-amount">Custom<span className="pk-price-per"> pricing</span></div>
            <ul className="pk-price-features">
              <li>Everything in Professional, plus:</li>
              <li>50+ tokens per year (or unlimited)</li>
              <li>Full white-label reports</li>
              <li>Multi-tenant org hierarchy</li>
              <li>API access for workflow integration</li>
              <li>Dedicated Customer Success Manager</li>
              <li>On-premise / dedicated cloud deployment</li>
              <li>DPDP / GDPR-compliant data residency</li>
            </ul>
            <Link to="/contact?p=assayo" className="btn btn-ghost pk-price-cta" data-testid="pk-cta-enterprise">Contact Sales <ArrowRight size={16} /></Link>
          </div>
        </div>
        <div className="pk-price-footer mono" data-testid="pk-price-footer">
          <span>{"Every new account gets 1 free token \u2014 run your first assessment at zero cost."}</span>
          <span>{"Payments via Razorpay (India) or Stripe (global) \u00b7 GST-compliant invoicing \u00b7 Tokens never expire"}</span>
        </div>
      </div>
    </section>
  );
}

/* ═══════ S11 — FAQ ═══════════════════════════════════════════ */
function S11Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section className="grantha-faq pk-faq" data-testid="pk-faq">
      <div className="container">
        <div className="eyebrow">Frequently asked</div>
        <h2 className="grantha-faq-h" data-testid="pk-faq-h2">Everything a <em>transformation leader</em> wants to know.</h2>
        <div className="grantha-faq-list" data-testid="pk-faq-list">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className={"grantha-faq-item " + (isOpen ? "is-open" : "")} data-testid={`pk-faq-${i}`}>
                <button type="button" onClick={() => setOpen(isOpen ? -1 : i)} className="grantha-faq-q" aria-expanded={isOpen}>
                  <span>{f.q}</span>
                  {isOpen ? <Minus size={18} /> : <Plus size={18} />}
                </button>
                {isOpen && <div className="grantha-faq-a">{f.a}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ═══════ S12 — CTA FOOTER ════════════════════════════════════ */
function S12Cta() {
  return (
    <section className="pk-cta-footer" data-testid="pk-cta-footer">
      <div className="container pk-cta-inner">
        <div className="eyebrow" style={{ color: "#F26B1F" }}>{"Run your first assessment \u2014 free"}</div>
        <h2 className="pk-cta-h" data-testid="pk-cta-h2">
          {"Your first maturity assessment is free. "}<em>{"Your next transformation shouldn\u2019t be a guess."}</em>
        </h2>
        <p className="pk-cta-sub" data-testid="pk-cta-sub">
          {"Every new account gets one free token \u2014 enough for a complete assessment of any of our seventeen industry-grade templates. No credit card required. No commitment. Run the diagnostic that answers \u201chow mature are we, really?\u201d \u2014 then see the AI-generated roadmap and ROI case before you pay anything."}
        </p>
        <div className="pk-cta-btns" data-testid="pk-cta-btns">
          <Link to="/contact?p=assayo" className="btn pk-cta-btn-primary" data-testid="pk-cta-start">{"Start Free Assessment \u2192"}</Link>
          <Link to="/contact?p=assayo" className="btn btn-ghost pk-cta-btn-ghost" data-testid="pk-cta-book">Book a 30-min walkthrough with our team</Link>
        </div>
      </div>
    </section>
  );
}
